import type { FocusTrap, Options } from 'focus-trap';

import { useEffect, useSyncExternalStore } from 'react';

const knownDialogs = new Set<HTMLElement>();
const trappedDialogs = new Set<HTMLElement>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach(listener => listener());

const dialogSelector =
  '[role="dialog"]:not([data-reach-dialog-content]):not([data-app-focus-lock])';

const log = (message: string, element?: HTMLElement) =>
  // eslint-disable-next-line no-console
  console.info.apply(undefined, ['Focus trap:', message, element].filter(Boolean));

const isOneTrustDialog = (element: HTMLElement) =>
  element.id.startsWith('onetrust') || element.closest('[id^="onetrust"]') !== null;

const closeControlSelector = '[class*="close" i], [aria-label*="close" i]';
const isElementVisible = (element: HTMLElement) => {
  if (!document.body.contains(element) || element.getAttribute('aria-hidden') === 'true') {
    return false;
  }

  const style = getComputedStyle(element);

  if (style.visibility === 'hidden') {
    return false;
  }

  const rect = element.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.right > 0 &&
    rect.bottom > 0 &&
    rect.left < viewportWidth &&
    rect.top < viewportHeight
  );
};

const activeFocusTraps = new Map<HTMLElement, { trap: FocusTrap; cleanup: () => void }>();

const reassertOneTrustPriority = () => {
  activeFocusTraps.forEach(({ trap }, node) => {
    if (isOneTrustDialog(node) && trap.paused) {
      trap.unpause();
    }
  });
};

const activateFocusTrap = async (node: HTMLElement) => {
  if (activeFocusTraps.has(node)) {
    return;
  }

  log('activated for', node);

  const focusTrapOptions: Options = {
    initialFocus: () => node.querySelector<HTMLElement>(closeControlSelector) || undefined,
    allowOutsideClick: () => true,
  };

  const { createFocusTrap } = await import('focus-trap');

  if (!trappedDialogs.has(node)) {
    return;
  }

  try {
    const focusTrap = createFocusTrap(node, focusTrapOptions);

    focusTrap.activate();

    activeFocusTraps.set(node, {
      trap: focusTrap,
      cleanup: () => {
        focusTrap.deactivate();
        log('deactivated for', node);
      },
    });

    reassertOneTrustPriority();
  } catch {
    log('activation failed for', node);
    trappedDialogs.delete(node);
    notify();
  }
};

const deactivateFocusTrap = (node: HTMLElement) => {
  const entry = activeFocusTraps.get(node);

  if (!entry) {
    return;
  }

  const { cleanup } = entry;

  activeFocusTraps.delete(node);
  cleanup();
};

const refreshVisibility = () => {
  let changed = false;

  knownDialogs.forEach(element => {
    if (!document.body.contains(element)) {
      knownDialogs.delete(element);
      trappedDialogs.delete(element);
      deactivateFocusTrap(element);
      changed = true;

      return;
    }

    const wasTrapped = trappedDialogs.has(element);
    const isVisible = isElementVisible(element);

    if (isVisible === wasTrapped) {
      return;
    }

    changed = true;

    if (isVisible) {
      trappedDialogs.add(element);
      activateFocusTrap(element);
    } else {
      trappedDialogs.delete(element);
      deactivateFocusTrap(element);
    }
  });

  reassertOneTrustPriority();

  if (changed) {
    notify();
  }
};

const collectDialogs = (root: HTMLElement) => {
  if (root.matches(dialogSelector)) {
    knownDialogs.add(root);
  }

  root.querySelectorAll<HTMLElement>(dialogSelector).forEach(dialog => {
    knownDialogs.add(dialog);
  });
};

const checkNewNodes: globalThis.MutationCallback = mutations => {
  mutations.forEach(mutation => {
    mutation.addedNodes.forEach(node => {
      if (node instanceof HTMLElement) {
        collectDialogs(node);
      }
    });

    if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
      collectDialogs(mutation.target);
    }
  });

  refreshVisibility();
};

/**
 * Observer to focus trap external dialogs injected by exponea (and other
 * externally-injected widgets, e.g. a CMP) - re-checks visibility on any DOM
 * or style/class/aria-hidden change so dialogs hidden via a collapsed
 * ancestor (not removed from the DOM) are correctly detected as inactive.
 */
export const useExternalDialogFocusTrap = () => {
  useEffect(() => {
    log('Observer init');

    collectDialogs(document.body);
    refreshVisibility();

    const observer = new MutationObserver(checkNewNodes);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['role', 'style', 'class', 'aria-hidden'],
    });

    return () => {
      observer.disconnect();
      log('Observer disconnected');
    };
  }, []);
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);

  return () => listeners.delete(listener);
};

const getSnapshot = () => trappedDialogs.size > 0;
const getServerSnapshot = () => false;

/**
 * True while an externally injected dialog (e.g. Exponea, a CMP) is holding the
 * focus trap. In-app focus locks should disable themselves while this is true so
 * external dialogs always take priority over locale selector / newsletter / cookie
 * wall traps.
 */
export const useIsExternalDialogTrapped = (): boolean =>
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
